import { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ExpandMore,
  TravelExploreOutlined,
  MenuBookOutlined,
  FormatQuoteOutlined,
} from "@mui/icons-material";

import SearchedTier from "./SearchedTier";
import DocumentSourceTier from "./DocumentSourceTier";
import ProvenanceExportButton from "./ProvenanceExportButton";
import { countProvenanceTiers } from "./provenanceCounts";
import type { AssistantResponseSources } from "../../types/provenance";

type ProvenancePanelProps = {
  provenance: AssistantResponseSources;
  isLoading?: boolean;
  /**
   * Message coordinates enabling the provenance export (spec Story 6). When
   * absent (legacy embedded surfaces), the export control is hidden.
   */
  dialogueId?: number | null;
  messageId?: string | null;
};

type TierId = "searched" | "read" | "cited";

const TierHeader = ({
  icon,
  title,
  count,
  extra,
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
  /** Optional trailing adornment (e.g. a degradation label chip). */
  extra?: React.ReactNode;
}) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
    {icon}
    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {extra}
    <Box sx={{ flex: 1 }} />
    <Chip
      label={count}
      size="small"
      variant="outlined"
      sx={{ height: "20px", fontSize: "0.7rem" }}
    />
  </Stack>
);

// When a tier is expanded it should claim the panel's remaining height and
// scroll its OWN content, so the other two tier headers stay pinned and visible
// (rather than the whole panel scrolling and pushing them out of view). This
// reaches into MUI's Accordion internals (Collapse → region → details) to make
// that chain a flex column whose details pane is the scroll container.
const expandedTierSx = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  "& .MuiCollapse-root": { flex: 1, minHeight: 0, overflow: "hidden" },
  "& .MuiCollapse-wrapper": { height: "100%" },
  "& .MuiCollapse-wrapperInner": {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  "& .MuiAccordion-region": {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  "& .MuiAccordionDetails-root": { flex: 1, minHeight: 0, overflowY: "auto" },
} as const;

/**
 * Three-tier source provenance panel (Searched → Read → Cited).
 *
 * Renders the funnel as a stack of collapsible accordions. Backward
 * compatible: any empty tier still renders with an explanatory empty state so
 * the panel "shows what we have and labels what's missing" (spec §9.1).
 * Degradation notices from `payload.degradation` are rendered VERBATIM
 * (contract §Graceful degradation): `no_documents` replaces the tiers with one
 * centered message, `subagent_untraced` shows a warning banner, `no_citations`
 * prefers the payload copy for the Cited empty state, and
 * `response_level_attribution` labels the Cited tier header.
 */
const ProvenancePanel = ({
  provenance,
  isLoading,
  dialogueId,
  messageId,
}: ProvenancePanelProps) => {
  const cited = provenance.assistant_response_sources ?? [];
  const read = provenance.read_sources ?? [];
  const searched = provenance.searched ?? [];
  const degradation = provenance.degradation ?? null;

  // Unavailable entries (contract §document_unavailable) keep their tier: a
  // cited one holds its citation_index slot; a read one renders unnumbered.
  const unavailable = provenance.unavailable_sources ?? [];
  const citedUnavailable = useMemo(
    () => unavailable.filter((u) => u.cited),
    [unavailable],
  );
  // Nested funnel (Read ⊇ Cited): the Read tier lists EVERYTHING read into
  // context, so it receives cited chunks + read chunks (deduped per-document by
  // the grouping) and every unavailable slot. Cited sources thus appear in both
  // Read and Cited — you can't cite what you didn't read.
  const readTierSources = useMemo(() => [...read, ...cited], [read, cited]);

  // External (ADAMS / OSTI / web) sources split by whether the answer cited
  // them (#20423 A2): CITED externals are promoted into the Cited tier as
  // numbered rows (their `citation_index`); every other external was read into
  // context but not cited, so it joins the Read tier (#20423 web-read).
  // Counts come from the SHARED helper so this panel and the per-response
  // summary line can never disagree again (#20423 audit).
  const {
    citedCount,
    readCount,
    searchedCollectionCount: searchedCount,
    citedExternal,
    readExternal,
  } = useMemo(() => countProvenanceTiers(provenance), [provenance]);

  // Default the Cited tier open (the primary signal); collapse the others.
  const [expanded, setExpanded] = useState<Record<TierId, boolean>>({
    searched: false,
    read: false,
    cited: true,
  });

  // Exclusive expansion: opening a tier collapses the others so the open tier
  // claims the panel's full height and scrolls internally (rather than two
  // half-height tiers sharing the space). Clicking the open tier closes it.
  const toggle = (id: TierId) =>
    setExpanded((prev) => ({
      searched: false,
      read: false,
      cited: false,
      [id]: !prev[id],
    }));

  if (isLoading) {
    return (
      <Box
        data-testid="aa-src-panel-loading"
        sx={{ display: "flex", justifyContent: "center", py: 4 }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  // No documents consulted at all → the backend's verbatim notice, no tiers
  // (contract degradation matrix).
  if (degradation?.no_documents) {
    return (
      <Box
        data-testid="aa-provenance-panel"
        sx={{ display: "flex", justifyContent: "center", px: 2, py: 6 }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          data-testid="aa-src-no-documents"
        >
          {degradation.no_documents}
        </Typography>
      </Box>
    );
  }

  return (
    // Fixed-height flex column so an expanded tier can claim the leftover space
    // and scroll internally while the tier headers stay pinned. The sidebar's
    // persistent-Drawer ancestors aren't height-anchored to the viewport, so we
    // anchor here (viewport minus the app + sidebar header chrome above).
    <Box
      data-testid="aa-provenance-panel"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 170px)",
        overflow: "hidden",
      }}
    >
      {degradation?.subagent_untraced && (
        <Alert
          severity="warning"
          data-testid="aa-src-subagent-untraced"
          sx={{ mx: 1, mt: 1, py: 0, fontSize: "0.75rem", flexShrink: 0 }}
        >
          {degradation.subagent_untraced}
        </Alert>
      )}

      {dialogueId != null && messageId && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1.5, py: 1, flexShrink: 0 }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="aa-src-summary"
          >
            {searchedCount} searched · {readCount} read · {citedCount} cited
          </Typography>
          <ProvenanceExportButton
            dialogueId={dialogueId}
            messageId={messageId}
          />
        </Stack>
      )}

      <Accordion
        expanded={expanded.searched}
        onChange={() => toggle("searched")}
        disableGutters
        data-testid="aa-src-tier-searched"
        sx={expanded.searched ? expandedTierSx : undefined}
      >
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ flexShrink: 0 }}>
          <TierHeader
            icon={
              <TravelExploreOutlined
                sx={{ fontSize: "1.1rem", color: "text.secondary" }}
              />
            }
            title="Searched"
            count={searchedCount}
          />
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <SearchedTier
            entries={searched}
            citedSources={cited}
            readSources={read}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded.read}
        onChange={() => toggle("read")}
        disableGutters
        data-testid="aa-src-tier-read"
        sx={expanded.read ? expandedTierSx : undefined}
      >
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ flexShrink: 0 }}>
          <TierHeader
            icon={
              <MenuBookOutlined
                sx={{ fontSize: "1.1rem", color: "text.secondary" }}
              />
            }
            title="Read"
            count={readCount}
          />
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <DocumentSourceTier
            sources={readTierSources}
            variant="read"
            emptyTitle="Nothing read"
            emptyDescription="No passages were read into context for this response."
            provenance={provenance}
            unavailable={unavailable}
            readExternals={readExternal}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded.cited}
        onChange={() => toggle("cited")}
        disableGutters
        data-testid="aa-src-tier-cited"
        sx={expanded.cited ? expandedTierSx : undefined}
      >
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ flexShrink: 0 }}>
          <TierHeader
            icon={
              <FormatQuoteOutlined
                sx={{ fontSize: "1.1rem", color: "primary.main" }}
              />
            }
            title="Cited"
            count={citedCount}
            extra={
              degradation?.response_level_attribution ? (
                // Verbatim backend copy; the chip ellipsizes visually and the
                // tooltip carries the full sentence.
                <Tooltip title={degradation.response_level_attribution} arrow>
                  <Chip
                    label={degradation.response_level_attribution}
                    size="small"
                    variant="outlined"
                    color="warning"
                    data-testid="aa-src-response-level"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      height: "20px",
                      fontSize: "0.65rem",
                      maxWidth: 180,
                    }}
                  />
                </Tooltip>
              ) : undefined
            }
          />
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <DocumentSourceTier
            sources={cited}
            variant="cited"
            emptyTitle={
              degradation?.no_citations ?? "No citations for this response"
            }
            emptyDescription="The assistant did not cite any sources in this answer."
            provenance={provenance}
            unavailable={citedUnavailable}
            citedExternals={citedExternal}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ProvenancePanel;
